import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule, TitleCasePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';

interface DocEntry {
  id: string;
  section: string;
  title: string;
  shortcut?: string;
  /** One-line purpose; always shown first. */
  summary: string;
  /** Optional short bullets for scan-friendly detail. */
  bullets?: string[];
  tags: string[];
}

@Component({
  selector: 'app-docs',
  standalone: true,
  imports: [CommonModule, FormsModule, TitleCasePipe],
  templateUrl: './docs.html',
  styleUrls: ['./docs.css']
})
export class DocsPage implements OnInit, OnDestroy {
  @ViewChild('docsSearchInput', { static: true }) private docsSearchInput?: ElementRef<HTMLInputElement>;

  theme: 'pxlvd' | 'dark' | 'light' = 'pxlvd';

  private readonly onSearchHotkey = (ev: KeyboardEvent): void => {
    if (ev.key !== 'k' && ev.key !== 'K') return;
    if (!ev.ctrlKey && !ev.metaKey) return;
    if (ev.altKey || ev.shiftKey) return;
    const t = ev.target as HTMLElement | null;
    if (t?.isContentEditable) return;
    if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA') && t !== this.docsSearchInput?.nativeElement) {
      return;
    }
    ev.preventDefault();
    ev.stopImmediatePropagation();
    const input = this.docsSearchInput?.nativeElement;
    if (input) {
      input.focus();
      input.select();
    }
  };

  ngOnInit(): void {
    const saved = localStorage.getItem('pxlvd-theme') as any;
    this.theme = saved || 'pxlvd';
    document.body.setAttribute('data-theme', this.theme);
    document.addEventListener('keydown', this.onSearchHotkey, true);
  }

  ngOnDestroy(): void {
    document.removeEventListener('keydown', this.onSearchHotkey, true);
  }

  setTheme(t: 'pxlvd' | 'dark' | 'light'): void {
    this.theme = t;
    localStorage.setItem('pxlvd-theme', t);
    document.body.setAttribute('data-theme', t);
  }

  search = '';
  activeSection = 'all';

  sections = ['all', 'tools', 'selection', 'symmetry', 'layers', 'animation', 'import', 'export', 'interface', 'shortcuts'];

  readonly sectionLabels: Record<string, string> = {
    tools: 'Drawing tools',
    selection: 'Selection & clipboard',
    symmetry: 'Symmetry',
    layers: 'Layers',
    animation: 'Animation',
    import: 'Import',
    export: 'Export',
    interface: 'Workspace & collaboration',
    shortcuts: 'Keyboard reference'
  };

  docs: DocEntry[] = [
    { id: 'draw', section: 'tools', title: 'Draw', shortcut: 'B',
      summary: 'Paint pixels or strokes on the active layer; respects symmetry and brush size.',
      bullets: ['Brush size presets 1–8px in the panel; [ and ] adjust up to 16px.', 'Drag to paint continuously.'],
      tags: ['draw', 'paint', 'brush', 'pixel'] },
    { id: 'erase', section: 'tools', title: 'Erase', shortcut: 'E',
      summary: 'Same as draw but writes the background color (#000000).',
      tags: ['erase', 'delete', 'remove', 'brush'] },
    { id: 'fill', section: 'tools', title: 'Fill', shortcut: 'G',
      summary: 'Flood-fill one contiguous color region with the active color.',
      tags: ['fill', 'bucket', 'flood', 'paint'] },
    { id: 'pick', section: 'tools', title: 'Pick (eyedropper)', shortcut: 'I',
      summary: 'Sample a canvas pixel and set it as the active color.',
      bullets: ['Lives in the Draw tools row next to draw, erase, fill, and select.'],
      tags: ['pick', 'eyedropper', 'sample', 'color', 'pipette'] },
    { id: 'select', section: 'selection', title: 'Select', shortcut: 'S',
      summary: 'Rectangular marquee: drag to create, drag inside to move the box.',
      bullets: ['Floating toolbar: copy, cut, fill, clear, flip, rotate.'],
      tags: ['select', 'rectangle', 'marquee'] },
    { id: 'move', section: 'selection', title: 'Move', shortcut: 'M',
      summary: 'Move selected pixels; source is cleared. Needs an existing selection (or drag inside a selection with Select active).',
      tags: ['move', 'drag', 'translate'] },
    { id: 'selection-ops', section: 'selection', title: 'Selection shortcuts',
      summary: 'Clipboard and transforms from the keyboard.',
      bullets: [
        'Ctrl+C copy · Ctrl+X cut · Ctrl+V paste (stamps float if present)',
        'Del / Backspace: clear selection when no float',
        'R / Shift+R: rotate 90° CW / CCW · Ctrl+A: select all'
      ],
      tags: ['copy', 'cut', 'paste', 'rotate', 'flip', 'delete', 'selection'] },
    { id: 'floating-clip', section: 'selection', title: 'Floating clipboard & shapes',
      summary: 'After copy, cut, or a shape drag (line, rect, circle, ellipse, triangle), a floating preview appears before you commit.',
      bullets: [
        'Drag inside to move; corner/edge handles resize.',
        'W×H, R in the bar or tools panel; Enter or ✓ Place commits; ✕ discards.',
        'Esc commits a float; arrows nudge 1px (Shift+5px).',
        'Polygon: no float - vertices commit straight to the layer.'
      ],
      tags: ['clipboard', 'paste', 'floating', 'copy', 'shape', 'enter'] },
    { id: 'symmetry-basic', section: 'symmetry', title: 'Basic symmetry',
      summary: 'None, vertical, horizontal, quad, and diagonal mirror strokes across axes.',
      bullets: ['Applies to draw, erase, fill, shapes, and polygon.'],
      tags: ['symmetry', 'mirror', 'reflect', 'vertical', 'horizontal'] },
    { id: 'symmetry-radial', section: 'symmetry', title: 'Radial symmetry',
      summary: '3×–12× repetition around the canvas center (mandala-style).',
      tags: ['radial', 'mandala', 'symmetry', 'rotate', 'pattern'] },
    { id: 'symmetry-star', section: 'symmetry', title: 'Star symmetry',
      summary: '5 / 6 / 8-point stars mirror and rotate strokes into arms.',
      tags: ['star', 'symmetry', 'mandala', 'pattern'] },
    { id: 'shapes', section: 'tools', title: 'Shapes',
      summary: 'Line, rectangle, circle, ellipse, triangle: drag to preview, release for a floating shape; outline or filled under Shapes.',
      bullets: ['Dimensions → Place at center for exact sizes.', 'Polygon is a separate tool (vertices, double-click to close).'],
      tags: ['shapes', 'line', 'rectangle', 'circle', 'ellipse', 'triangle', 'polygon'] },
    { id: 'polygon-tool', section: 'tools', title: 'Polygon',
      summary: 'Click vertices; double-click closes and strokes edges to the active layer with current color and symmetry.',
      bullets: ['✕ Cancel on the hint drops points without closing.'],
      tags: ['polygon', 'path', 'vertices', 'click'] },
    { id: 'layers', section: 'layers', title: 'Layers',
      summary: 'Per-frame stack, top-down composite. Painting targets the selected layer.',
      bullets: ['Double-click name to rename · drag handles to reorder.'],
      tags: ['layers', 'stack', 'composite'] },
    { id: 'layers-ops', section: 'layers', title: 'Layer options',
      summary: 'Visibility, lock, celluloid (continuous), opacity, blend (normal / multiply / screen / overlay), tint, hold duration.',
      bullets: ['Merge Down merges the active layer into the one below.'],
      tags: ['opacity', 'blend', 'tint', 'merge', 'layer', 'lock'] },
    { id: 'onion-skin', section: 'layers', title: 'Onion skin',
      summary: 'Optional neighbor-frame ghosts while drawing (pink = before, green = after).',
      bullets: ['Frame count 1–5 and opacity in the Layers panel.'],
      tags: ['onion', 'ghost', 'animation', 'previous', 'next'] },
    { id: 'frames', section: 'animation', title: 'Frames & timeline',
      summary: 'Add or duplicate frames, drag thumbnails to reorder; FPS lives in Settings.',
      tags: ['frames', 'animation', 'timeline', 'fps'] },
    { id: 'tween', section: 'animation', title: 'Keyframe tween',
      summary: 'Right sidebar: pick FROM / TO frames and steps; new frames interpolate pixel colors between them.',
      tags: ['tween', 'interpolate', 'keyframe', 'animation'] },
    { id: 'undo-redo', section: 'tools', title: 'Undo / redo', shortcut: 'Ctrl+Z / Ctrl+Y',
      summary: 'Snapshot history; depth 20 / 50 / 100 / 200 in Settings.',
      bullets: ['New projects and some imports reset history.'],
      tags: ['undo', 'redo', 'history'] },
    { id: 'brush-size', section: 'tools', title: 'Brush size', shortcut: '[ ]',
      summary: 'Panel presets 1–8px; [ and ] step size (keyboard up to 16px).',
      bullets: ['Each size paints an N×N block per step.'],
      tags: ['brush', 'size', 'stroke'] },
    { id: 'import-sidebar-image', section: 'import', title: 'Import image',
      summary: 'Sidebar → Import → Image: place with drag or X/Y, scale, opacity, then commit onto the frame.',
      tags: ['import', 'image', 'png', 'jpeg', 'photo'] },
    { id: 'import-sidebar-video', section: 'import', title: 'Import video',
      summary: 'Sidebar → Import → Video: MP4, WebM, MOV, or AVI → pixel frames (FPS, dither, max frames).',
      bullets: ['Honors canvas size and palette options in the panel.'],
      tags: ['import', 'video', 'frames', 'mp4', 'webm'] },
    { id: 'export-spritesheet', section: 'export', title: 'Spritesheet PNG',
      summary: 'File → Export → Spritesheet: grid of all frames; pick columns and 1×–8× scale.',
      tags: ['export', 'spritesheet', 'png', 'sprite'] },
    { id: 'export-gif', section: 'export', title: 'GIF / PNG sequence',
      summary: 'File → Export → GIF: animated GIF (FPS, scale, transparency, optional dither) or PNG frame sequence.',
      tags: ['export', 'gif', 'sequence', 'animation'] },
    { id: 'export-image', section: 'export', title: 'Image export',
      summary: 'PNG, WebP, JPG, or SVG: scale, transparency (non-JPEG), quality, one frame or ZIP of all.',
      tags: ['export', 'png', 'webp', 'jpg', 'svg'] },
    { id: 'export-video', section: 'export', title: 'Video export',
      summary: 'File → Export → Video: Mediabunny + browser codecs (MP4, WebM, MOV, MKV as supported).',
      bullets: ['FPS, target width, progress bar while encoding.'],
      tags: ['export', 'video', 'mp4', 'webm', 'mediabunny'] },
    { id: 'export-pxlvd', section: 'export', title: '.pxlvd project',
      summary: 'JSON project: frames, layers, settings, palettes - File → Save / Save As and the project manager.',
      tags: ['save', 'load', 'project', 'file', 'pxlvd'] },
    { id: 'collaboration', section: 'interface', title: 'Collaboration',
      summary: 'Real-time rooms: Collab panel for profile, create/join (optional password), peers, requests, and chat when enabled.',
      bullets: [
        'HTTP may need local signaling (command shown in the panel).',
        'Canvas size can lock while in a room.',
        'Admins get a Settings tab - see “Room settings (admin)”.'
      ],
      tags: ['collab', 'multiplayer', 'room', 'sync', 'webrtc'] },
    { id: 'collab-admin-settings', section: 'interface', title: 'Room settings (admin)',
      summary: 'Collab → Settings: Save applies edits; Reset discards unsaved changes.',
      bullets: [
        'Frame: admin-only create / delete - limit who adds or removes frames.',
        'Members: draw-only (paint only) · allow save/export snapshots (optional).',
        'Sync: optional sharing of new frames created by peers into the shared project · cursor name tags · room chat on/off.',
        'Security: optional room password · max users 2–50 · auto-rejoin after refresh.',
        'Banned list with Unban.'
      ],
      tags: ['collab', 'admin', 'settings', 'permissions', 'chat'] },
    { id: 'settings-overview', section: 'interface', title: 'Settings',
      summary: 'Menu → Settings: resolution, cell size, grid, palette family & count, custom palettes, theme, FPS, undo depth, interface toggles.',
      bullets: ['Resolution may be read-only during collab.'],
      tags: ['settings', 'resolution', 'theme', 'palette', 'grid'] },
    { id: 'canvas-hint-bar', section: 'interface', title: 'Canvas shortcut hints',
      summary: 'Bottom-right chevron toggles a compact hint strip (zoom, fit, tools, undo, etc.).',
      bullets: ['Default collapsed; persisted in IndexedDB.', 'Settings → Interface can expand hints by default.'],
      tags: ['hints', 'shortcuts', 'ui', 'chevron', 'preferences'] },
    { id: 'number-fields', section: 'interface', title: 'Number fields',
      summary: 'Themed stepper buttons instead of browser spinners, shared border for a cleaner panel.',
      tags: ['input', 'stepper', 'ui'] },
    { id: 'shortcuts', section: 'shortcuts', title: 'Shortcuts overview',
      summary: 'Quick reference; tool shortcuts also appear on the canvas hint bar when expanded.',
      bullets: [
        'Tools: B E G I S M (draw, erase, fill, pick, select, move)',
        'Canvas: F fit · Space drag / middle-mouse pan · wheel zoom',
        'Frames: A add blank (when allowed)',
        'Edit: Ctrl+Z/Y redo variants · Ctrl+A · Ctrl+C/X/V · Del · R / Shift+R · [ ]',
        'Float: Enter / ✓ Place · arrows nudge · Ctrl+V stamp · Esc commits float or clears selection/polygon'
      ],
      tags: ['shortcuts', 'keyboard', 'hotkeys', 'reference'] }
  ];

  get filtered(): DocEntry[] {
    const q = this.search.toLowerCase().trim();
    return this.docs.filter(d => {
      const matchSec = this.activeSection === 'all' || d.section === this.activeSection;
      const hay = [
        d.title,
        d.summary,
        ...(d.bullets ?? []),
        ...d.tags
      ].join(' ').toLowerCase();
      const matchQ = !q || hay.includes(q);
      return matchSec && matchQ;
    });
  }

  get docGroups(): { section: string; label: string; docs: DocEntry[] }[] {
    const list = this.filtered;
    const order = this.sections.filter(s => s !== 'all');
    if (this.activeSection !== 'all') {
      return [{
        section: this.activeSection,
        label: this.sectionLabels[this.activeSection] ?? this.activeSection,
        docs: list
      }];
    }
    const map = new Map<string, DocEntry[]>();
    for (const s of order) map.set(s, []);
    for (const d of list) {
      const bucket = map.get(d.section);
      if (bucket) bucket.push(d);
    }
    return order
      .map(s => ({
        section: s,
        label: this.sectionLabels[s] ?? s,
        docs: map.get(s) ?? []
      }))
      .filter(g => g.docs.length > 0);
  }

  get showSectionHeadings(): boolean {
    return this.activeSection === 'all';
  }

  highlight(text: string): string {
    if (!this.search.trim()) return text;
    const q = this.search.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp(`(${q})`, 'gi');
    return text.replace(re, '<mark>$1</mark>');
  }
}
